import { useEffect, useRef, useState } from "react"

export const getFractionalIndexForElapsed = (elapsedMs: number, timestamps: number[]): number => {
    if (timestamps.length === 0) return 0
    const target = timestamps[0] + elapsedMs

    let lo = 0
    let hi = timestamps.length - 1
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1
        if (timestamps[mid] <= target) lo = mid
        else hi = mid - 1
    }

    const t =
        lo < timestamps.length - 1
            ? Math.max(
                  0,
                  Math.min(1, (target - timestamps[lo]) / (timestamps[lo + 1] - timestamps[lo]))
              )
            : 0
    return lo + t
}

export const useTelemetryPlayback = ({
    timestamps,
    autoPlay = false,
}: {
    timestamps: number[]
    autoPlay?: boolean
}) => {
    const rafRef = useRef<number | null>(null)
    const playbackIndexRef = useRef(0)
    const elapsedRef = useRef(0)
    const rafStartWallRef = useRef(0)
    const rafStartOffsetRef = useRef(0)
    const speedRef = useRef(1)

    const [playbackIndex, setPlaybackIndex] = useState(0)
    const [isPlaying, setIsPlaying] = useState(autoPlay)
    const [speed, setSpeed] = useState(1)
    const [isSeeking, setIsSeeking] = useState(false)

    useEffect(() => {
        if (!isPlaying || isSeeking || timestamps.length === 0) return

        rafStartWallRef.current = performance.now()
        rafStartOffsetRef.current = elapsedRef.current

        const tick = () => {
            const elapsed =
                (performance.now() - rafStartWallRef.current) * speedRef.current +
                rafStartOffsetRef.current
            elapsedRef.current = elapsed

            const fractionalIndex = getFractionalIndexForElapsed(elapsed, timestamps)
            const lo = Math.floor(fractionalIndex)
            playbackIndexRef.current = lo
            setPlaybackIndex(fractionalIndex)

            if (lo >= timestamps.length - 1) {
                setIsPlaying(false)
                return
            }

            rafRef.current = requestAnimationFrame(tick)
        }

        rafRef.current = requestAnimationFrame(tick)
        return () => {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
        }
    }, [isPlaying, isSeeking, timestamps])

    const handlePlayPause = () => {
        if (timestamps.length === 0) return
        if (!isPlaying && playbackIndexRef.current >= timestamps.length - 1) {
            playbackIndexRef.current = 0
            elapsedRef.current = 0
            setPlaybackIndex(0)
        }
        setIsPlaying((p) => !p)
    }

    const handleSeek = (fractionalIndex: number) => {
        const lo = Math.floor(fractionalIndex)
        const hi = Math.min(lo + 1, timestamps.length - 1)
        const t = fractionalIndex - lo
        playbackIndexRef.current = lo
        elapsedRef.current =
            timestamps.length > 0
                ? timestamps[lo] + t * (timestamps[hi] - timestamps[lo]) - timestamps[0]
                : 0
        setPlaybackIndex(fractionalIndex)
    }

    const handleSeekStart = () => setIsSeeking(true)
    const handleSeekEnd = () => setIsSeeking(false)

    const handleSetSpeed = (nextSpeed: number) => {
        if (isPlaying) {
            rafStartWallRef.current = performance.now()
            rafStartOffsetRef.current = elapsedRef.current
        }
        speedRef.current = nextSpeed
        setSpeed(nextSpeed)
    }

    return {
        playbackIndex,
        isPlaying,
        speed,
        handlePlayPause,
        handleSeek,
        handleSeekStart,
        handleSeekEnd,
        handleSetSpeed,
    }
}
